import { useEffect, useRef } from 'react';
import { OnResize, resizers } from 'utils';

/**
 * Attaches a resizing observer to the element to call the `cb` with the provided context `ctx` on element resize.
 * Both `cb` and `ctx` can be volatile, that is `useCallback` is not required for the stable hook dependency.
 */
export const useElementResized = <TElement extends HTMLElement, Extra extends {} = {}>(
  element: TElement | null | undefined,
  options?: {
    cb?: OnResize<Extra>;
    ctx?: Extra;
  }
) => {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (element) {
      const onRowResize: OnResize<Extra> = (entry, ctx) =>
        optionsRef.current?.cb?.(entry, { ...ctx, ...optionsRef.current?.ctx });

      resizers.add<Extra>(element, {
        ...(optionsRef.current?.ctx as Extra),
        cb: onRowResize,
      });
    }

    return () => {
      if (element) resizers.remove(element);
    };
  }, [element]);
};
